"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState, useRef, useEffect } from "react";
import ShoppingCart from "./Shopping_cart";
import CtaWhatsApp from "./home/ctaWhatsApp";
import Recherche from "./Recherche";
import { API_BASE_URL, getImageUrl } from '@/app/lib/api';
import { FiMenu, FiSearch, FiChevronDown, FiHeart, FiUser, FiX } from "react-icons/fi";
import { usePathname } from "next/navigation";
import { useWishlist } from "./wishlist/WishlistContext";

// --- TYPES ---
type ApiCategory = {
  id: string;
  name: string;
  slug: string;
  image?: string | null;
  imageThum?: string | null;
  children?: ApiCategory[];
};

export default function Header() {
  const pathname = usePathname();
  const [activeMenu, setActiveMenu] = useState<'products' | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<Record<string, boolean>>({});
  const hoverTimer = useRef<number | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileGrid, setShowMobileGrid] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);

  // Data states
  const [parents, setParents] = useState<ApiCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // --- FETCH CATEGORIES ---
  useEffect(() => {
    let active = true;
    async function loadCategories() {
      try {
        setLoadingCategories(true);
        const exclude = encodeURIComponent("meilleures-ventes,nos-coups-de-coeur");
        const resp = await fetch(`${API_BASE_URL}/categories/roots/children?exclude=${exclude}`);
        if (!resp.ok) throw new Error(`Erreur API`);
        const data = await resp.json();
        if (active) {
          setParents(data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (active) {
          setLoadingCategories(false);
        }
      }
    }
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  // --- SCROLL EFFECT ---
  useEffect(() => {
    const handleScroll = () => setHasShadow(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenuEnter = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setActiveMenu('products');
  };

  const handleMenuLeave = () => {
    hoverTimer.current = window.setTimeout(() => setActiveMenu(null), 150);
  };

  return (
    <>
      <CtaWhatsApp />

      {/* HEADER PRINCIPAL */}
      <header
        className={`bg-main sticky top-0 z-50 transition-all duration-300 ${hasShadow ? 'shadow-xl py-2' : 'py-4'
          }`}
      >
        <div className="container max-w-screen-2xl mx-auto px-4 lg:px-8 flex items-center justify-between">

          {/* MOBILE: Burger Menu */}
          <button
            onClick={() => setShowMobileGrid(!showMobileGrid)}
            className="md:hidden text-white p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            {showMobileGrid ? <FiX size={26} /> : <FiMenu size={26} />}
          </button>

          {/* LOGO */}
          <div className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
            <Link href="/" className="block">
              <Image
                src="/logo.png.webp"
                alt="Logo"
                width={160}
                height={45}
                className="w-20 md:w-24 lg:w-28 h-auto object-contain"
                priority
              />
            </Link>
          </div>

          {/* DESKTOP NAVIGATION */}
          <nav
            className="hidden md:flex items-center justify-center flex-1 px-4 lg:px-10"
            onMouseLeave={handleMenuLeave}
          >
            <ul className="flex items-center space-x-1 lg:space-x-8">
              <li>
                <NavLink href="/" active={pathname === "/"}>Accueil</NavLink>
              </li>

              <li className="relative group" onMouseEnter={handleMenuEnter}>
                <Link
                  href="/nos-produits"
                  className={`flex items-center gap-1 text-white font-semibold text-sm lg:text-xl py-2 px-3 rounded-md transition-all hover:bg-white/10 ${activeMenu === 'products' ? 'bg-white/10' : ''
                    }`}
                >
                  Nos Produits
                  <FiChevronDown className={`transition-transform duration-300 ${activeMenu === 'products' ? 'rotate-180' : ''}`} />
                </Link>
              </li>

              <li>
                <NavLink href="/contact" active={pathname === "/contact"}>Contact</NavLink>
              </li>
            </ul>
          </nav>

          {/* ACTIONS (Search, Icons) */}
          <div className="flex items-center gap-1 md:gap-3">
            <HeaderAction icon={<FiSearch size={22} />} label="Recherche" onClick={() => setShowSearch(true)} />
            <WishlistIcon label="Favoris" />
            <div className="pl-2">
              <ShoppingCart />
            </div>
          </div>
        </div>

        {/* --- MEGA MENU DESKTOP --- */}
        {activeMenu === 'products' && (
          <div
            className="hidden md:block absolute left-0 top-full w-full bg-main shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300"
            onMouseEnter={handleMenuEnter}
            onMouseLeave={handleMenuLeave}
          >
            <div className="container max-w-screen-2xl mx-auto p-8">
              <div className="flex justify-between items-end mb-8">
                <h3 className="text-2xl font-bold text-white">Nos Catégories</h3>
                <Link href="/nos-produits" className="text-white/70 hover:text-white text-sm underline underline-offset-4" onClick={() => setActiveMenu(null)}>
                  Voir tous les produits
                </Link>
              </div>

              {loadingCategories ? (
                <div className="grid grid-cols-6 gap-4 animate-pulse">
                  {[...Array(6)].map((_, i) => <div key={i} className="bg-white/10 aspect-square rounded-xl" />)}
                </div>
              ) : (
                <div className="grid grid-cols-4 lg:grid-cols-6 xl:grid-cols-10 gap-6">
                  {parents.map((cat) => (
                    <Link
                      key={cat.id}
                      href={`/categorie/${cat.slug}`}
                      className="group flex flex-col items-center text-center"
                      onClick={() => setActiveMenu(null)}
                    >
                      <div className="relative w-full aspect-square rounded-2xl overflow-hidden mb-3 ring-1 ring-white/10 group-hover:ring-white/40 transition-all shadow-lg">
                        <Image
                          src={(cat.imageThum || cat.image) ? getImageUrl(cat.imageThum || cat.image) : "/placeholder.png"}
                          alt={cat.name}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
                      </div>
                      <span className="text-lg font-medium text-white group-hover:text-secondary transition-colors line-clamp-2 px-1 capitalize">
                        {cat.name}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- MOBILE DRAWER --- */}
        <div className={`md:hidden absolute top-full left-0 w-full bg-main shadow-2xl transition-all duration-300 overflow-y-auto ${showMobileGrid ? "max-h-[90vh] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
          }`}>
          <div className="p-4 space-y-2">
            <MobileNavLink href="/" onClick={() => setShowMobileGrid(false)}>Accueil</MobileNavLink>

            <div className="relative">
              <MobileNavLink href="/nos-produits" onClick={() => setShowMobileGrid(false)}>
                Nos Produits
              </MobileNavLink>
              <button
                onClick={() => setExpandedMobile(prev => ({ ...prev, p: !prev.p }))}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white"
              >
                <FiChevronDown className={`transition-transform ${expandedMobile['p'] ? 'rotate-180' : ''}`} />
              </button>

              <div className={`grid grid-cols-3 gap-3 px-2 transition-all duration-300 overflow-hidden ${expandedMobile['p'] ? 'max-h-[1000px] py-4' : 'max-h-0'
                }`}>
                {parents.map((cat) => (
                  <Link key={cat.id} href={`/categorie/${cat.slug}`} className="flex flex-col items-center gap-2" onClick={() => setShowMobileGrid(false)}>
                    <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-white/10">
                      <Image src={(cat.imageThum || cat.image) ? getImageUrl(cat.imageThum || cat.image) : "/placeholder.png"} alt={cat.name} fill className="object-cover" />
                    </div>
                    <span className="text-[10px] text-white text-center line-clamp-1">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <MobileNavLink href="/contact" onClick={() => setShowMobileGrid(false)}>Contact</MobileNavLink>
          </div>
        </div>
        
        {/* --- WAVE EFFECT --- */}
        <div className="absolute top-full left-0 w-full overflow-hidden leading-[0] pointer-events-none">
          <svg
            className="relative block w-full h-[20px] md:h-[40px]"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V120H0V56.44Z"
              style={{ fill: '#a91b8d' }}
            ></path>
          </svg>
        </div>
      </header>

      {showSearch && <Recherche onClose={() => setShowSearch(false)} />}
    </>
  );
}

// --- SUB-COMPONENTS HELPER ---

function NavLink({ href, children, active }: { href: string; children: React.ReactNode; active?: boolean }) {
  return (
    <Link
      href={href}
      className={`relative px-3 py-2 text-sm lg:text-xl font-semibold transition-colors hover:text-white/80 group ${active ? 'text-white' : 'text-white/90'
        }`}
    >
      {children}
      <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-white transition-transform duration-300 origin-left ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
        }`} />
    </Link>
  );
}

function HeaderAction({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center justify-center min-w-[50px] md:min-w-[64px] group text-white">
      <div className="p-2 group-hover:bg-white/10 rounded-full transition-all group-hover:-translate-y-0.5">
        {icon}
      </div>
      <span className="hidden md:block text-[10px] font-medium opacity-70 group-hover:opacity-100 transition-opacity">
        {label}
      </span>
    </button>
  );
}

function MobileNavLink({ href, children, onClick }: { href: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="block p-4 text-white font-bold text-lg rounded-xl hover:bg-white/5 transition-colors"
    >
      {children}
    </Link>
  );
}

// Gestion Wishlist (version sécurisée)
function WishlistIcon({ label }: { label: string }) {
  const { count } = useWishlist();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Link href="/wishlist" className="flex flex-col items-center justify-center min-w-[50px] md:min-w-[64px] group text-white">
      <div className="relative p-2 group-hover:bg-white/10 rounded-full transition-all group-hover:-translate-y-0.5">
        <FiHeart size={22} />
        {mounted && count > 0 && (
          <span className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-bold h-4 w-4 rounded-full flex items-center justify-center animate-in zoom-in duration-300">
            {count}
          </span>
        )}
      </div>
      <span className="hidden md:block text-[10px] font-medium opacity-70 group-hover:opacity-100 transition-opacity">
        {label}
      </span>
    </Link>
  );
}

function AccountIcon({ label }: { label: string }) {
  const [user, setUser] = useState<{ name?: string } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE_URL}/users/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => setUser({ name: data.name ?? data.nom }))
        .catch(() => setUser(null));
    }
  }, []);

  const displayLabel = user ? (user.name?.split(' ')[0] ?? 'Profil') : label;

  return (
    <Link href={user ? '/account' : '/login'} className="flex flex-col items-center justify-center min-w-[50px] md:min-w-[64px] group text-white">
      <div className="p-2 group-hover:bg-white/10 rounded-full transition-all group-hover:-translate-y-0.5">
        <FiUser size={22} />
      </div>
      <span className="hidden md:block text-[10px] font-medium opacity-70 group-hover:opacity-100 transition-opacity truncate max-w-[60px]">
        {displayLabel}
      </span>
    </Link>
  );
}